import React, { useState } from 'react'
import { PageHeader } from '@/components/ui/PageHeader'
import { useNavigate } from 'react-router-dom'
import SlaSettings from './SlaSettings'
import ScorecardTemplates from './ScorecardTemplates'
import RateCalculatorSettings from './RateCalculatorSettings'

// Feature #262: the "Integration Sequences" tab moved out of TAP
// Settings and became the Lab-wide /automation page. We keep a small
// shim entry on the tab bar that just navigates the user there, so
// existing muscle memory and bookmarks still find the feature.

type TabKey = 'sla' | 'scorecards' | 'rateCalculator'

const tabs: { key: TabKey; label: string }[] = [
  { key: 'sla', label: 'SLA Settings' },
  { key: 'scorecards', label: 'Scorecard Templates' },
  // Feature #229: live rate calculator inputs.
  { key: 'rateCalculator', label: 'Rate Calculator' },
]

const TapSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('sla')
  const navigate = useNavigate()

  return (
    <div style={{ padding: '0' }}>
      <PageHeader
        title="TAP Settings"
        subtitle="Configure TAP module settings"
      />

      {/* Tab bar */}
      <div style={{
        display: 'flex',
        gap: '0',
        borderBottom: '2px solid var(--color-light-grey)',
        backgroundColor: 'var(--color-white)',
        flexWrap: 'wrap',
      }}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '12px 20px',
              border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid var(--color-magenta)' : '2px solid transparent',
              backgroundColor: 'transparent',
              color: activeTab === tab.key ? 'var(--color-magenta)' : 'var(--color-dark-grey)',
              fontWeight: activeTab === tab.key ? 600 : 400,
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              marginBottom: '-2px',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'sla' && <SlaSettings embedded />}
        {activeTab === 'scorecards' && <ScorecardTemplates embedded />}
        {activeTab === 'rateCalculator' && <RateCalculatorSettings embedded />}
      </div>

      {/* Feature #262: shortcut to the Lab-wide automation page that
          replaces the old "Integration Sequences" tab on this page.
          Kept on the page so existing muscle memory finds the
          feature. */}
      <div style={{ marginTop: '24px', padding: '16px', backgroundColor: 'var(--color-light-grey)', borderRadius: '8px' }}>
        <div style={{ fontSize: '13px', color: 'var(--color-dark-grey)', marginBottom: '8px' }}>
          The "Integration Sequences" tab moved out of TAP Settings — it's now a Lab-wide feature.
        </div>
        <button
          onClick={() => navigate('/automation/sequences')}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--color-magenta)',
            color: 'var(--color-white)',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: 'var(--font-family)',
          }}
        >
          Open Automation Sequences →
        </button>
      </div>
    </div>
  )
}

export default TapSettings
